import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Download, Mail, Calendar, Users, FileText, Image as ImageIcon, Video, Link as LinkIcon, Archive, Building2, Twitter, Youtube, Twitch, Instagram, Music, Globe } from "lucide-react";
import { StudioName } from "@/components/StudioName";
import { toast } from "sonner";
import JSZip from "jszip";
import { Helmet } from "react-helmet-async";

interface PressKit {
  id: string;
  game_id: string;
  email: string; // Email stored directly in the table
  content: {
    title: string;
    description: string;
    screenshots: string[];
    trailer?: string;
    links: {
      steam?: string;
      website?: string;
      twitter?: string;
      discord?: string;
      [key: string]: string | undefined;
    };
    contact: {
      email?: string;
      name?: string;
      studio_name?: string;
      studio_logo?: string;
      country?: string;
    };
    features: string[];
    release_date?: string;
    genres: string[];
    price?: number;
    studio_description?: string;
    studio_social_media?: {
      twitch?: string;
      youtube?: string;
      tiktok?: string;
      instagram?: string;
      twitter?: string;
      bluesky?: string;
      steam?: string;
      website?: string;
      [key: string]: string | undefined;
    };
  };
  created_at: string;
  game?: {
    title: string;
    cover_image_url?: string;
    developer?: {
      display_name: string;
      studio_name?: string;
      avatar_url?: string;
      website_url?: string;
      is_premium?: boolean;
      premium_end_date?: string | null;
    };
  };
}

export default function PressKit() {
  const { gameId } = useParams<{ gameId: string }>();
  const [pressKit, setPressKit] = useState<PressKit | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    if (gameId) {
      fetchPressKit();
    }
  }, [gameId]);


  const fetchPressKit = async () => {
    try {
      const { data, error } = await supabase
        .from("presskits")
        .select(`
          *,
          game:games!presskits_game_id_fkey(
            title,
            cover_image_url,
            developer:profiles!games_developer_id_fkey(
              display_name,
              studio_name,
              avatar_url,
              website_url,
              is_premium,
              premium_end_date
            )
          )
        `)
        .eq("game_id", gameId)
        .eq("is_public", true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          toast.error("Press kit not found");
        } else {
          console.error("Error fetching press kit:", error);
          toast.error("Error loading press kit");
        }
        setPressKit(null);
      } else {
        setPressKit(data as PressKit);
      }
    } catch (error) {
      console.error("Error fetching press kit:", error);
      toast.error("Error loading press kit");
      setPressKit(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "TBA";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string): string => {
    if (!url) return '';

    // Handle different YouTube URL formats
    // https://www.youtube.com/watch?v=VIDEO_ID
    // https://youtu.be/VIDEO_ID
    // https://www.youtube.com/embed/VIDEO_ID

    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (videoIdMatch && videoIdMatch[1]) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }

    // If already an embed URL, return as is
    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    return url;
  };

  const getTextContent = () => {
    if (!pressKit) return '';

    return `
PRESS KIT - ${pressKit.content.title}
${'='.repeat(50)}

${pressKit.content.description.replace(/<[^>]*>/g, '')}

RELEASE DATE: ${formatDate(pressKit.content.release_date)}
GENRES: ${pressKit.content.genres.join(', ')}
${pressKit.content.price ? `PRICE: €${pressKit.content.price}` : ''}

FEATURES:
${pressKit.content.features.map(f => `- ${f}`).join('\n')}

LINKS:
${Object.entries(pressKit.content.links).map(([key, value]) => value ? `${key.toUpperCase()}: ${value}` : '').filter(Boolean).join('\n')}

CONTACT:
${pressKit.content.contact?.name ? `Name: ${pressKit.content.contact.name}` : ''}
${pressKit.content.contact?.studio_name ? `Studio: ${pressKit.content.contact.studio_name}` : ''}
Email: ${pressKit.email}
${pressKit.content.contact?.country ? `Country: ${pressKit.content.contact.country}` : ''}

${pressKit.content.studio_description ? `\nSTUDIO DESCRIPTION:\n${pressKit.content.studio_description.replace(/<[^>]*>/g, '')}\n` : ''}

${pressKit.content.studio_social_media && Object.keys(pressKit.content.studio_social_media).length > 0 ? `STUDIO SOCIAL MEDIA:\n${Object.entries(pressKit.content.studio_social_media).map(([key, value]) => value ? `${key.toUpperCase()}: ${value}` : '').filter(Boolean).join('\n')}\n` : ''}

Generated on ${new Date(pressKit.created_at).toLocaleDateString()}
    `.trim();
  };

  const handleDownloadPressKit = () => {
    if (!pressKit) return;

    const textContent = getTextContent();
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pressKit.content.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_presskit.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Press kit downloaded!");
  };

  const handleDownloadZip = async () => {
    if (!pressKit) return;

    setDownloadingZip(true);
    try {
      const zip = new JSZip();

      // Add text file
      const textContent = getTextContent();
      zip.file("presskit.txt", textContent);

      // Helper function to fetch image and add to zip
      const addImageToZip = async (url: string, filename: string) => {
        try {
          const response = await fetch(url);
          if (response.ok) {
            const blob = await response.blob();
            zip.file(filename, blob);
            return true;
          }
        } catch (error) {
          console.warn(`Failed to fetch ${url}:`, error);
        }
        return false;
      };

      // Add screenshots
      if (pressKit.content.screenshots && pressKit.content.screenshots.length > 0) {
        const screenshotsFolder = zip.folder("screenshots");
        if (screenshotsFolder) {
          for (let i = 0; i < pressKit.content.screenshots.length; i++) {
            const screenshot = pressKit.content.screenshots[i];
            const extension = screenshot.split('.').pop()?.split('?')[0] || 'jpg';
            await addImageToZip(screenshot, `screenshots/screenshot_${i + 1}.${extension}`);
          }
        }
      }

      // Add studio logo if exists (from contact or developer avatar_url)
      const studioLogoForZip = pressKit.content.contact?.studio_logo || pressKit.game?.developer?.avatar_url;
      if (studioLogoForZip) {
        const extension = studioLogoForZip.split('.').pop()?.split('?')[0] || 'png';
        await addImageToZip(studioLogoForZip, `logo.${extension}`);
      }

      // Add cover image if exists
      if (pressKit.game?.cover_image_url) {
        const extension = pressKit.game.cover_image_url.split('.').pop()?.split('?')[0] || 'jpg';
        await addImageToZip(pressKit.game.cover_image_url, `cover.${extension}`);
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pressKit.content.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_presskit.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Press kit ZIP downloaded!");
    } catch (error) {
      console.error("Error creating ZIP:", error);
      toast.error("Failed to create ZIP file");
    } finally {
      setDownloadingZip(false);
    }
  };

  if (loading) {
    return (
      <Layout hideHeaderIfLoggedOut hideFooterIfLoggedOut>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!pressKit) {
    return (
      <Layout hideHeaderIfLoggedOut hideFooterIfLoggedOut>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-2">Press Kit Not Found</h1>
            <p className="text-muted-foreground">
              This press kit is not available or has been removed.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  // Get studio logo (from contact or developer avatar_url)
  const studioLogo = pressKit.content.contact?.studio_logo || pressKit.game?.developer?.avatar_url;

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const title = `${pressKit.content.title} - Press Kit | IndieKeys`;
  const description = pressKit.content.description.replace(/<[^>]*>/g, '').substring(0, 200);
  const imageUrl = pressKit.game?.cover_image_url || '';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />

        {/* Open Graph tags for Facebook, Discord, etc. */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        {imageUrl && (
          <>
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/jpeg" />
          </>
        )}

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      </Helmet>
      <Layout hideHeaderIfLoggedOut hideFooterIfLoggedOut>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-4xl font-bold mb-2">{pressKit.content.title}</h1>
                {pressKit.game?.developer && (
                  <p className="text-lg text-muted-foreground">
                    by <StudioName
                      studioName={pressKit.game.developer.studio_name}
                      displayName={pressKit.game.developer.display_name}
                      isPremium={pressKit.game.developer.is_premium}
                      premiumEndDate={pressKit.game.developer.premium_end_date}
                    />
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {/* <Button onClick={handleDownloadPressKit} variant="outline">
                
                Download TXT
              </Button> */}
                <Button onClick={handleDownloadZip} variant="default" disabled={downloadingZip}>
                  {downloadingZip ? (
                    <>
                      <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Creating ZIP...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download ZIP
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT COLUMN: Info + Description at the end */}
            <div className="space-y-6">
              {/* Key Information */}
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">Information</h2>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold">Release Date</h3>
                    </div>
                    <p className="text-muted-foreground">{formatDate(pressKit.content.release_date)}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold">Genres</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pressKit.content.genres.map((genre, index) => (
                        <Badge key={index} variant="outline">{genre}</Badge>
                      ))}
                    </div>
                  </div>

                  {pressKit.content.price !== undefined && pressKit.content.price > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Price</h3>
                      </div>
                      <p className="text-muted-foreground">€{pressKit.content.price}</p>
                    </div>
                  )}

                  {pressKit.content.links?.steam && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                        </svg>
                        <h3 className="font-semibold">Steam</h3>
                      </div>
                      <a
                        href={pressKit.content.links.steam}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {pressKit.content.links.steam}
                      </a>
                    </div>
                  )}
                </div>
              </Card>

              {/* Features */}
              {pressKit.content.features && pressKit.content.features.length > 0 && (
                <Card className="p-6">
                  <h2 className="text-2xl font-bold mb-4">Features</h2>
                  <ul className="space-y-2">
                    {pressKit.content.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Contact */}
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Mail className="w-6 h-6" />
                  Contact
                </h2>
                <div className="space-y-3">
                  {pressKit.content.contact?.name && (
                    <div>
                      <span className="font-semibold">Name: </span>
                      <span className="text-muted-foreground">{pressKit.content.contact.name}</span>
                    </div>
                  )}

                  {pressKit.content.contact?.studio_name ? (
                    <div>
                      <span className="font-semibold">Studio: </span>
                      <span className="text-muted-foreground">{pressKit.content.contact.studio_name}</span>
                    </div>
                  ) : pressKit.game?.developer?.studio_name ? (
                    <div>
                      <span className="font-semibold">Studio: </span>
                      <span className="text-muted-foreground">{pressKit.game.developer.studio_name}</span>
                    </div>
                  ) : pressKit.game?.developer?.display_name ? (
                    <div>
                      <span className="font-semibold">Developer: </span>
                      <span className="text-muted-foreground">{pressKit.game.developer.display_name}</span>
                    </div>
                  ) : null}

                  <div>
                    <span className="font-semibold">Email: </span>
                    <a
                      href={`mailto:${pressKit.email}`}
                      className="text-primary hover:underline"
                    >
                      {pressKit.email}
                    </a>
                  </div>

                  {pressKit.content.contact?.country && (
                    <div>
                      <span className="font-semibold">Country: </span>
                      <span className="text-muted-foreground">{pressKit.content.contact.country}</span>
                    </div>
                  )}

                </div>
              </Card>

              {/* Studio Description and Social Media */}
              {(pressKit.content.studio_description || (pressKit.content.studio_social_media && Object.keys(pressKit.content.studio_social_media).length > 0)) && (
                <Card className="p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Building2 className="w-6 h-6" />
                    Studio
                  </h2>
                  <div className="space-y-4">
                    {pressKit.content.studio_description && (
                      <div>
                        <h3 className="font-semibold mb-2">About the Studio</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {pressKit.content.studio_description.replace(/<[^>]*>/g, '')}
                        </p>
                      </div>
                    )}

                    {pressKit.content.studio_social_media && Object.keys(pressKit.content.studio_social_media).length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <LinkIcon className="w-4 h-4" />
                          Social Media
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          {pressKit.content.studio_social_media.website && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.website} target="_blank" rel="noopener noreferrer">
                                <Globe className="w-4 h-4 mr-2" />
                                Website
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.twitter && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.twitter} target="_blank" rel="noopener noreferrer">
                                <Twitter className="w-4 h-4 mr-2" />
                                Twitter/X
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.youtube && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.youtube} target="_blank" rel="noopener noreferrer">
                                <Youtube className="w-4 h-4 mr-2" />
                                YouTube
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.twitch && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.twitch} target="_blank" rel="noopener noreferrer">
                                <Twitch className="w-4 h-4 mr-2" />
                                Twitch
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.instagram && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.instagram} target="_blank" rel="noopener noreferrer">
                                <Instagram className="w-4 h-4 mr-2" />
                                Instagram
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.tiktok && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.tiktok} target="_blank" rel="noopener noreferrer">
                                <Music className="w-4 h-4 mr-2" />
                                TikTok
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.bluesky && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.bluesky} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Bluesky
                              </a>
                            </Button>
                          )}
                          {pressKit.content.studio_social_media.steam && (
                            <Button
                              variant="outline"
                              className="w-full justify-start"
                              asChild
                            >
                              <a href={pressKit.content.studio_social_media.steam} target="_blank" rel="noopener noreferrer">
                                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                                </svg>
                                Steam
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Description at the end */}
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">About</h2>
                <div
                  className="prose prose-invert max-w-none text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: pressKit.content.description }}
                />
              </Card>
            </div>

            {/* RIGHT COLUMN: Media */}
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <ImageIcon className="w-6 h-6" />
                  Media
                </h2>
                <div className="space-y-6">
                  {/* Studio Logo */}
                  {studioLogo && (
                    <div>
                      <h3 className="font-semibold mb-3">Studio Logo</h3>
                      <div className="bg-muted rounded-lg p-4 flex items-center justify-center">
                        <img
                          src={studioLogo}
                          alt="Studio logo"
                          className="max-h-32 max-w-full object-contain"
                        />
                      </div>
                    </div>
                  )}

                  {/* Cover Image */}
                  {pressKit.game?.cover_image_url && (
                    <div>
                      <h3 className="font-semibold mb-3">Cover Image</h3>
                      <img
                        src={pressKit.game.cover_image_url}
                        alt={pressKit.content.title}
                        className="w-full rounded-lg"
                      />
                    </div>
                  )}

                  {/* Screenshots */}
                  {pressKit.content.screenshots && pressKit.content.screenshots.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Screenshots</h3>
                      <div className="grid grid-cols-1 gap-4">
                        {pressKit.content.screenshots.map((screenshot, index) => (
                          <a
                            key={index}
                            href={screenshot}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group"
                          >
                            <img
                              src={screenshot}
                              alt={`${pressKit.content.title} screenshot ${index + 1}`}
                              className="w-full rounded-lg hover:scale-105 transition-transform"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trailer */}
                  {pressKit.content.trailer && (
                    <div>
                      <h3 className="font-semibold mb-3">Trailer</h3>
                      <div className="aspect-video">
                        <iframe
                          src={getYouTubeEmbedUrl(pressKit.content.trailer)}
                          className="w-full h-full rounded-lg"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>Press kit generated with Indie Keys on {formatDate(pressKit.created_at)}</p>
          </div>
        </div>
      </Layout>
    </>
  );
}
